import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { 
  Search, 
  Plus, 
  Upload, 
  MoreVertical, 
  ArrowLeft,
  UserCheck,
  UserX,
  Edit,
  Filter,
  X,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  Download,
  FileText,
  AlertTriangle,
  Loader2,
  ChevronDown,
  ChevronUp,
  Key
} from 'lucide-react';
import { toast } from 'sonner';

const CITIES = ["Mumbai", "Bangalore", "Delhi", "Chennai", "Hyderabad", "Pune", "Kolkata", "Ahmedabad"];
// Updated: 6 roles - Admin deprecated, CRM Support added
const ROLES = [
  { value: "Rep", label: "Rep" },
  { value: "Manager", label: "Manager" },
  { value: "city_admin", label: "City Admin" },
  { value: "strategy_support_admin", label: "Strategy Support Admin" },
  { value: "crm_support", label: "CRM Support" },
  { value: "super_admin", label: "Super Admin" }
];

const AdminUsers = ({ user }) => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [search, setSearch] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [managers, setManagers] = useState([]);  // For reporting_manager dropdown
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    role: 'Rep',
    city: '',
    is_active: true,
    reporting_manager_id: '',  // For hierarchy
    // New optional fields
    supervisor_id: '',
    date_of_joining: '',
    date_of_birth: '',
    go_live_date: ''
  });
  
  // Bulk upload state
  const [csvContent, setCsvContent] = useState('');
  const [bulkStep, setBulkStep] = useState('upload'); // upload, preview, results
  const [previewData, setPreviewData] = useState(null);
  const [bulkResults, setBulkResults] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [expandedErrors, setExpandedErrors] = useState(false);
  
  // Password reset state
  const [showResetPasswordDialog, setShowResetPasswordDialog] = useState(false);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);

  // Bulk assign manager state
  const [showBulkAssignDialog, setShowBulkAssignDialog] = useState(false);
  const [unassignedReps, setUnassignedReps] = useState([]);
  const [selectedReps, setSelectedReps] = useState([]);
  const [bulkAssignManagerId, setBulkAssignManagerId] = useState('');
  const [bulkAssignLoading, setBulkAssignLoading] = useState(false);
  const [unassignedCity, setUnassignedCity] = useState('');

  // Check if current user is city_admin (can only manage their city)
  const isCityAdmin = user?.role === 'city_admin';
  const userCity = user?.city;

  // If city_admin, restrict city filter to their city
  useEffect(() => {
    if (isCityAdmin && userCity) {
      setCityFilter(userCity);
    }
  }, [isCityAdmin, userCity]);

  // Fetch managers for reporting_manager dropdown
  const fetchManagers = useCallback(async (city = '') => {
    try {
      const params = new URLSearchParams();
      if (city) params.append('city', city);
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/managers?${params.toString()}`,
        {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setManagers(data.managers || []);
      }
    } catch (error) {
      console.error('Failed to fetch managers:', error);
    }
  }, []);

  // Fetch managers when city changes in form
  useEffect(() => {
    if (formData.city) {
      fetchManagers(formData.city);
    }
  }, [formData.city, fetchManagers]);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Session expired. Please login again.');
        navigate('/login');
        return;
      }

      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (cityFilter) params.append('city', cityFilter);
      if (roleFilter) params.append('role', roleFilter);
      if (statusFilter !== '') params.append('is_active', statusFilter);

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/users?${params.toString()}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.status === 401) {
        toast.error('Session expired. Please login again.');
        localStorage.removeItem('token');
        navigate('/login');
        return;
      }
      
      if (response.status === 403) {
        setLoadError('You do not have permission to view users.');
        toast.error('You do not have permission to view users.');
        return;
      }
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Server error: ${response.status}`);
      }
      
      const data = await response.json();
      setUsers(data.users || []);
    } catch (error) {
      console.error('Failed to load users:', error);
      let errorMessage = 'Failed to load users';
      if (error.message.includes('NetworkError') || error.message.includes('Failed to fetch')) {
        errorMessage = 'Network error. Please check your connection.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      setLoadError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [search, cityFilter, roleFilter, statusFilter, navigate]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleAddUser = async () => {
    if (!formData.full_name || !formData.email || !formData.city) {
      toast.error('Please fill all required fields');
      return;
    }

    // City admin can only add users to their city
    if (isCityAdmin && formData.city !== userCity) {
      toast.error(`You can only add users to ${userCity}`);
      return;
    }

    // Warn if Rep doesn't have reporting_manager (but don't block)
    if (formData.role === 'Rep' && !formData.reporting_manager_id) {
      console.warn('Creating Rep without reporting_manager');
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Session expired. Please login again.');
        navigate('/login');
        return;
      }

      // Clean form data - only send non-empty values
      const cleanFormData = {
        full_name: formData.full_name,
        email: formData.email,
        role: formData.role,
        city: formData.city,
        is_active: formData.is_active
      };
      
      // Add optional fields only if they have values
      if (formData.phone?.trim()) cleanFormData.phone = formData.phone.trim();
      if (formData.reporting_manager_id) cleanFormData.reporting_manager_id = formData.reporting_manager_id;
      if (formData.supervisor_id) cleanFormData.supervisor_id = formData.supervisor_id;
      if (formData.date_of_joining) cleanFormData.date_of_joining = formData.date_of_joining;
      if (formData.date_of_birth) cleanFormData.date_of_birth = formData.date_of_birth;
      if (formData.go_live_date) cleanFormData.go_live_date = formData.go_live_date;

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(cleanFormData)
      });

      if (response.status === 401) {
        toast.error('Session expired. Please login again.');
        localStorage.removeItem('token');
        navigate('/login');
        return;
      }

      const data = await response.json();
      
      if (!response.ok) {
        toast.error(data.detail || 'Failed to create user');
        return;
      }

      toast.success('User created successfully! They can login using OTP.');
      setShowAddDialog(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      console.error('Create user error:', error);
      toast.error(error.message || 'Failed to create user. Please try again.');
    }
  };

  const handleEditUser = async () => {
    if (!selectedUser) return;

    // City admin can only edit users in their city
    if (isCityAdmin && selectedUser.city !== userCity) {
      toast.error(`You can only edit users in ${userCity}`);
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${selectedUser.user_id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify(formData)
        }
      );

      if (!response.ok) {
        const data = await response.json();
        toast.error(data.detail || 'Failed to update user');
        return;
      }

      toast.success('User updated successfully');
      setShowEditDialog(false);
      setSelectedUser(null);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const handleToggleStatus = async (targetUser) => {
    // City admin can only toggle users in their city
    if (isCityAdmin && targetUser.city !== userCity) {
      toast.error(`You can only manage users in ${userCity}`);
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${targetUser.user_id}/toggle-status`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }
      );

      const data = await response.json();
      
      if (!response.ok) {
        toast.error(data.detail || 'Failed to update status');
        return;
      }

      toast.success(data.message);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  // Password reset handler
  const openResetPasswordDialog = (targetUser) => {
    setResetPasswordUser(targetUser);
    setNewPassword('Welcome@123'); // Default password
    setShowResetPasswordDialog(true);
  };

  const handleResetPassword = async () => {
    if (!newPassword.trim() || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setResetPasswordLoading(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${resetPasswordUser.user_id}/reset-password`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({ new_password: newPassword })
        }
      );

      const data = await response.json();

      if (!response.ok) {
        toast.error(data.detail || 'Failed to reset password');
        setResetPasswordLoading(false);
        return;
      }

      toast.success(`Password reset for ${resetPasswordUser.email}`);
      setShowResetPasswordDialog(false);
      setResetPasswordUser(null);
      setNewPassword('');
    } catch (error) {
      toast.error('Failed to reset password');
    }

    setResetPasswordLoading(false);
  };

  // Bulk assign manager functions
  const fetchUnassignedReps = async (city = '') => {
    setBulkAssignLoading(true);
    try {
      const params = city ? `?city=${encodeURIComponent(city)}` : '';
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/users/unassigned-reps${params}`,
        {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }
      );
      const data = await response.json();
      if (response.ok) {
        setUnassignedReps(data.users || []);
      } else {
        toast.error(data.detail || 'Failed to fetch unassigned reps');
      }
    } catch (error) {
      toast.error('Failed to fetch unassigned reps');
    }
    setBulkAssignLoading(false);
  };

  const openBulkAssignDialog = () => {
    setShowBulkAssignDialog(true);
    setSelectedReps([]);
    setBulkAssignManagerId('');
    const city = isCityAdmin ? userCity : '';
    setUnassignedCity(city);
    fetchUnassignedReps(city);
    fetchManagers(city); // Also fetch managers for the selected city
  };

  const handleSelectAllReps = (checked) => {
    if (checked) {
      setSelectedReps(unassignedReps.map(r => r.user_id));
    } else {
      setSelectedReps([]);
    }
  };

  const handleSelectRep = (userId, checked) => {
    if (checked) {
      setSelectedReps([...selectedReps, userId]);
    } else {
      setSelectedReps(selectedReps.filter(id => id !== userId));
    }
  };

  const handleBulkAssignManager = async () => {
    if (selectedReps.length === 0) {
      toast.error('Please select at least one rep');
      return;
    }
    if (!bulkAssignManagerId) {
      toast.error('Please select a manager');
      return;
    }

    setBulkAssignLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/users/bulk-assign-manager`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            user_ids: selectedReps,
            reporting_manager_id: bulkAssignManagerId
          })
        }
      );

      const data = await response.json();
      if (response.ok) {
        toast.success(data.message);
        setSelectedReps([]);
        fetchUnassignedReps(unassignedCity);
        fetchUsers(); // Refresh main user list
      } else {
        toast.error(data.detail || 'Failed to assign manager');
      }
    } catch (error) {
      toast.error('Failed to assign manager');
    }
    setBulkAssignLoading(false);
  };

  const handleBulkUpload = async () => {
    if (!csvContent.trim()) {
      toast.error('Please upload or paste CSV content');
      return;
    }

    setBulkLoading(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/users/bulk-upload`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({ 
            csv_content: csvContent,
            skip_invalid: true
          })
        }
      );

      const data = await response.json();
      
      if (!response.ok) {
        toast.error(data.detail || 'Bulk upload failed');
        return;
      }

      setBulkResults(data);
      setBulkStep('results');
      
      if (data.created > 0 || data.updated > 0) {
        toast.success(`Processed: ${data.created} created, ${data.updated} updated`);
        fetchUsers();
      }
    } catch (error) {
      toast.error('Bulk upload failed');
    } finally {
      setBulkLoading(false);
    }
  };

  // Preview CSV before uploading
  const handlePreviewCSV = async () => {
    if (!csvContent.trim()) {
      toast.error('Please upload or paste CSV content');
      return;
    }

    setBulkLoading(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/users/bulk-upload/preview`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({ csv_content: csvContent })
        }
      );

      const data = await response.json();
      
      if (!response.ok) {
        toast.error(data.detail || 'Failed to parse CSV');
        return;
      }

      setPreviewData(data);
      setBulkStep('preview');
    } catch (error) {
      toast.error('Failed to preview CSV');
    } finally {
      setBulkLoading(false);
    }
  };

  // Download CSV template
  const handleDownloadTemplate = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/users/bulk-upload/template`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      if (!response.ok) {
        toast.error('Failed to download template');
        return;
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'user_upload_template.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Template downloaded');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  // Handle file input
  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (event) => {
      setCsvContent(event.target.result);
      toast.success(`File loaded: ${file.name}`);
    };
    reader.onerror = () => {
      toast.error('Failed to read file');
    };
    reader.readAsText(file);
  };

  // Reset bulk upload state
  const resetBulkUpload = () => {
    setCsvContent('');
    setBulkStep('upload');
    setPreviewData(null);
    setBulkResults(null);
    setExpandedErrors(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const resetForm = () => {
    setFormData({
      full_name: '',
      email: '',
      phone: '',
      role: 'Rep',
      city: isCityAdmin ? userCity : '',
      is_active: true,
      reporting_manager_id: '',
      supervisor_id: '',
      date_of_joining: '',
      date_of_birth: '',
      go_live_date: ''
    });
  };

  const openEditDialog = (targetUser) => {
    setSelectedUser(targetUser);
    setFormData({
      full_name: targetUser.full_name || targetUser.rep_name || '',
      email: targetUser.email,
      phone: targetUser.phone || '',
      role: targetUser.role,
      city: targetUser.city || '',
      is_active: targetUser.is_active !== false,
      reporting_manager_id: targetUser.reporting_manager_id || '',
      supervisor_id: targetUser.supervisor_id || '',
      date_of_joining: targetUser.date_of_joining || '',
      date_of_birth: targetUser.date_of_birth || '',
      go_live_date: targetUser.go_live_date || ''
    });
    // Fetch managers for the user's city
    if (targetUser.city) {
      fetchManagers(targetUser.city);
    }
    setShowEditDialog(true);
  };

  const clearFilters = () => {
    if (!isCityAdmin) setCityFilter('');
    setRoleFilter('');
    setStatusFilter('');
    setSearch('');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: '2-digit'
      });
    } catch {
      return '—';
    }
  };

  const formatRelativeTime = (dateStr) => {
    if (!dateStr) return 'Never';
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diffMs = now - date;
      const diffSecs = Math.floor(diffMs / 1000);
      const diffMins = Math.floor(diffSecs / 60);
      const diffHours = Math.floor(diffMins / 60);
      const diffDays = Math.floor(diffHours / 24);
      const diffWeeks = Math.floor(diffDays / 7);
      const diffMonths = Math.floor(diffDays / 30);

      if (diffSecs < 60) return 'Just now';
      if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
      if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
      if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
      if (diffWeeks < 4) return `${diffWeeks} week${diffWeeks > 1 ? 's' : ''} ago`;
      if (diffMonths < 12) return `${diffMonths} month${diffMonths > 1 ? 's' : ''} ago`;
      return formatDate(dateStr);
    } catch {
      return 'Never';
    }
  };

  const hasActiveFilters = (isCityAdmin ? false : cityFilter) || roleFilter || statusFilter !== '';

  // Get available cities for city_admin
  const availableCities = isCityAdmin ? [userCity] : CITIES;

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="px-4 py-4 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 -ml-2 hover:bg-slate-100 rounded-lg">
            <ArrowLeft size={20} className="text-slate-600" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-semibold text-slate-800">User Management</h1>
            <p className="text-xs text-slate-500">
              {users.length} users {isCityAdmin && `in ${userCity}`}
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={fetchUsers}
            className="h-9 w-9"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </Button>
        </div>
      </div>

      <div className="px-4 py-4 space-y-4">
        {/* Search & Filters Row */}
        <div className="flex flex-wrap gap-2">
          <div className="flex-1 min-w-[200px] relative">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search name, email, or Emp ID..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 h-10 rounded-lg"
            />
          </div>
          
          <Select value={roleFilter || "all"} onValueChange={(v) => setRoleFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="w-[130px] h-10 rounded-lg">
              <SelectValue placeholder="Role" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              {ROLES.map(role => (
                <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {!isCityAdmin && (
            <Select value={cityFilter || "all"} onValueChange={(v) => setCityFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[130px] h-10 rounded-lg">
                <SelectValue placeholder="City" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Cities</SelectItem>
                {CITIES.map(city => (
                  <SelectItem key={city} value={city}>{city}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          
          <Select value={statusFilter === "" ? "all" : statusFilter} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="w-[130px] h-10 rounded-lg">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="true">Active</SelectItem>
              <SelectItem value="false">Inactive</SelectItem>
            </SelectContent>
          </Select>

          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters} className="h-10 text-slate-500">
              <X size={16} className="mr-1" /> Clear
            </Button>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2">
          <Button 
            onClick={() => { resetForm(); setShowAddDialog(true); }}
            className="flex-1 h-10 bg-[#5B3DF5] hover:bg-[#4A2FE0] rounded-lg"
          >
            <Plus size={18} className="mr-2" />
            Add User
          </Button>
          <Button 
            variant="outline"
            onClick={openBulkAssignDialog}
            className="h-10 rounded-lg"
          >
            <UserCheck size={18} className="mr-2" />
            Assign Manager
          </Button>
          <Button 
            variant="outline"
            onClick={() => { setBulkResults(null); setCsvContent(''); setShowBulkDialog(true); }}
            className="h-10 rounded-lg"
          >
            <Upload size={18} className="mr-2" />
            Bulk Upload
          </Button>
        </div>

        {/* Users Table */}
        <Card className="bg-white border-0 shadow-sm rounded-xl overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#EF4444] mx-auto"></div>
              <p className="text-sm text-slate-500 mt-2">Loading users...</p>
            </div>
          ) : loadError ? (
            <div className="p-8 text-center">
              <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-3" />
              <p className="text-red-600 font-medium mb-2">Failed to load users</p>
              <p className="text-slate-500 text-sm mb-4">{loadError}</p>
              <Button
                variant="outline"
                onClick={fetchUsers}
                className="gap-2"
              >
                <RefreshCw size={16} />
                Try Again
              </Button>
            </div>
          ) : users.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-slate-500">No users found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-semibold whitespace-nowrap">Emp ID</TableHead>
                    <TableHead className="font-semibold">Name</TableHead>
                    <TableHead className="font-semibold">Email</TableHead>
                    <TableHead className="font-semibold">Role</TableHead>
                    <TableHead className="font-semibold">Designation</TableHead>
                    <TableHead className="font-semibold">City</TableHead>
                    <TableHead className="font-semibold">DOJ</TableHead>
                    <TableHead className="font-semibold">Supervisor</TableHead>
                    <TableHead className="font-semibold">Last Login</TableHead>
                    <TableHead className="font-semibold">Status</TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map(u => (
                    <TableRow key={u.user_id} className="hover:bg-slate-50">
                      <TableCell className="text-xs font-mono text-slate-500 whitespace-nowrap">
                        {u.emp_id || u.rep_id || '—'}
                      </TableCell>
                      <TableCell className="font-medium whitespace-nowrap">
                        {u.full_name || u.rep_name || '—'}
                      </TableCell>
                      <TableCell className="text-slate-600 text-sm">{u.email}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 text-xs font-medium rounded whitespace-nowrap ${
                          u.role === 'super_admin' ? 'bg-purple-100 text-purple-700' :
                          u.role === 'Admin' ? 'bg-blue-100 text-blue-700' :
                          u.role === 'city_admin' ? 'bg-amber-100 text-amber-700' :
                          u.role === 'Manager' ? 'bg-teal-100 text-teal-700' :
                          'bg-slate-100 text-slate-600'
                        }`}>
                          {ROLES.find(r => r.value === u.role)?.label || u.role}
                        </span>
                      </TableCell>
                      <TableCell className="text-slate-600 text-sm whitespace-nowrap">
                        {u.designation || '—'}
                      </TableCell>
                      <TableCell className="text-slate-600 text-sm">{u.city || '—'}</TableCell>
                      <TableCell className="text-slate-500 text-sm whitespace-nowrap">
                        {u.doj ? formatDate(u.doj) : '—'}
                      </TableCell>
                      <TableCell className="text-slate-600 text-sm whitespace-nowrap max-w-[150px] truncate" title={u.reporting_supervisor_name || ''}>
                        {u.reporting_supervisor_name || '—'}
                      </TableCell>
                      <TableCell 
                        className="text-slate-500 text-sm whitespace-nowrap"
                        title={u.last_login_at ? new Date(u.last_login_at).toLocaleString('en-IN') : ''}
                      >
                        {formatRelativeTime(u.last_login_at)}
                      </TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full whitespace-nowrap ${
                          u.is_active !== false 
                            ? 'bg-emerald-100 text-emerald-700' 
                            : 'bg-[#F3F0FF] text-[#5B3DF5]'
                        }`}>
                          {u.is_active !== false ? 'Active' : 'Inactive'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreVertical size={16} />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEditDialog(u)}>
                              <Edit size={14} className="mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleToggleStatus(u)}>
                              {u.is_active !== false ? (
                                <>
                                  <UserX size={14} className="mr-2" />
                                  Deactivate
                                </>
                              ) : (
                                <>
                                  <UserCheck size={14} className="mr-2" />
                                  Activate
                                </>
                              )}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openResetPasswordDialog(u)}>
                              <Key size={14} className="mr-2" />
                              Reset Password
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </Card>
      </div>

      {/* Add User Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add New User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Full Name <span className="text-[#5B3DF5]">*</span></Label>
              <Input
                value={formData.full_name}
                onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                placeholder="Enter full name"
              />
            </div>
            <div className="space-y-2">
              <Label>Email <span className="text-[#5B3DF5]">*</span></Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                placeholder="email@nobroker.in"
              />
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                placeholder="Phone number (optional)"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Role <span className="text-[#5B3DF5]">*</span></Label>
                <Select value={formData.role} onValueChange={(v) => setFormData({...formData, role: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.map(role => (
                      <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>City <span className="text-[#5B3DF5]">*</span></Label>
                <Select 
                  value={formData.city} 
                  onValueChange={(v) => setFormData({...formData, city: v})}
                  disabled={isCityAdmin}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select city" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableCities.map(city => (
                      <SelectItem key={city} value={city}>{city}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {/* Reporting Manager - shown for Rep role */}
            {formData.role === 'Rep' && formData.city && (
              <div className="space-y-2">
                <Label>Reporting Manager</Label>
                <Select 
                  value={formData.reporting_manager_id || "none"} 
                  onValueChange={(v) => setFormData({...formData, reporting_manager_id: v === "none" ? "" : v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select manager" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {managers.map(m => (
                      <SelectItem key={m.user_id} value={m.user_id}>
                        {m.full_name || m.rep_name} ({m.city || 'All Cities'})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">Assign reporting manager for hierarchy</p>
              </div>
            )}
            
            {/* Optional Fields Section */}
            <div className="border-t pt-4 mt-4">
              <p className="text-xs text-slate-500 mb-3 font-medium">Optional Information</p>
              
              {/* Supervisor Dropdown - full width */}
              <div className="space-y-1 mb-3">
                <Label className="text-xs">Supervisor</Label>
                <Select 
                  value={formData.supervisor_id || "none"} 
                  onValueChange={(v) => setFormData({...formData, supervisor_id: v === "none" ? "" : v})}
                >
                  <SelectTrigger className="h-9 text-sm">
                    <SelectValue placeholder="Select supervisor" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {managers.map(m => (
                      <SelectItem key={m.user_id} value={m.user_id}>
                        {m.full_name || m.rep_name} ({m.city || 'All Cities'})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-400">Select a manager/admin as supervisor</p>
              </div>
              
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Date of Joining</Label>
                  <Input
                    type="date"
                    value={formData.date_of_joining}
                    onChange={(e) => setFormData({...formData, date_of_joining: e.target.value})}
                    className="h-9 text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Date of Birth</Label>
                  <Input
                    type="date"
                    value={formData.date_of_birth}
                    onChange={(e) => setFormData({...formData, date_of_birth: e.target.value})}
                    className="h-9 text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Go Live Date</Label>
                  <Input
                    type="date"
                    value={formData.go_live_date}
                    onChange={(e) => setFormData({...formData, go_live_date: e.target.value})}
                    className="h-9 text-sm"
                  />
                </div>
              </div>
            </div>
            
            <div className="flex items-center justify-between py-2">
              <Label>Active</Label>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({...formData, is_active: checked})}
              />
            </div>
            <Button onClick={handleAddUser} className="w-full bg-[#5B3DF5] hover:bg-[#4A2FE0]">
              Create User
            </Button>
            <p className="text-xs text-slate-500 text-center">
              User will receive OTP on their email for first login
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Full Name</Label>
              <Input
                value={formData.full_name}
                onChange={(e) => setFormData({...formData, full_name: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input value={formData.email} disabled className="bg-slate-50" />
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Role</Label>
                <Select 
                  value={formData.role} 
                  onValueChange={(v) => setFormData({...formData, role: v})}
                  disabled={isCityAdmin}  // City admin cannot change roles
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.map(role => (
                      <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>City</Label>
                <Select 
                  value={formData.city} 
                  onValueChange={(v) => setFormData({...formData, city: v})}
                  disabled={isCityAdmin}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {availableCities.map(city => (
                      <SelectItem key={city} value={city}>{city}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {/* Reporting Manager - shown for Rep role in edit mode */}
            {formData.role === 'Rep' && formData.city && (
              <div className="space-y-2">
                <Label>Reporting Manager</Label>
                <Select 
                  value={formData.reporting_manager_id || "none"} 
                  onValueChange={(v) => setFormData({...formData, reporting_manager_id: v === "none" ? "" : v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select manager" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {managers.map(m => (
                      <SelectItem key={m.user_id} value={m.user_id}>
                        {m.full_name || m.rep_name} ({m.city || 'All Cities'})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {/* Optional Fields Section */}
            <div className="border-t pt-4 mt-4">
              <p className="text-xs text-slate-500 mb-3 font-medium">Optional Information</p>
              
              {/* Supervisor Dropdown - full width */}
              <div className="space-y-1 mb-3">
                <Label className="text-xs">Supervisor</Label>
                <Select 
                  value={formData.supervisor_id || "none"} 
                  onValueChange={(v) => setFormData({...formData, supervisor_id: v === "none" ? "" : v})}
                >
                  <SelectTrigger className="h-9 text-sm">
                    <SelectValue placeholder="Select supervisor" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {managers.map(m => (
                      <SelectItem key={m.user_id} value={m.user_id}>
                        {m.full_name || m.rep_name} ({m.city || 'All Cities'})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-400">Select a manager/admin as supervisor</p>
              </div>
              
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Date of Joining</Label>
                  <Input
                    type="date"
                    value={formData.date_of_joining}
                    onChange={(e) => setFormData({...formData, date_of_joining: e.target.value})}
                    className="h-9 text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Date of Birth</Label>
                  <Input
                    type="date"
                    value={formData.date_of_birth}
                    onChange={(e) => setFormData({...formData, date_of_birth: e.target.value})}
                    className="h-9 text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Go Live Date</Label>
                  <Input
                    type="date"
                    value={formData.go_live_date}
                    onChange={(e) => setFormData({...formData, go_live_date: e.target.value})}
                    className="h-9 text-sm"
                  />
                </div>
              </div>
            </div>
            
            <div className="flex items-center justify-between py-2">
              <Label>Active</Label>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({...formData, is_active: checked})}
              />
            </div>
            <Button onClick={handleEditUser} className="w-full bg-[#5B3DF5] hover:bg-[#4A2FE0]">
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Upload Dialog */}
      <Dialog open={showBulkDialog} onOpenChange={(open) => {
        setShowBulkDialog(open);
        if (!open) resetBulkUpload();
      }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload size={20} />
              Bulk Upload Users
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto space-y-4 pt-2">
            {/* Step indicator */}
            <div className="flex items-center gap-2 text-sm">
              <span className={`px-2 py-1 rounded ${bulkStep === 'upload' ? 'bg-[#5B3DF5] text-white' : 'bg-slate-100 text-slate-600'}`}>
                1. Upload
              </span>
              <span className="text-slate-300">→</span>
              <span className={`px-2 py-1 rounded ${bulkStep === 'preview' ? 'bg-[#5B3DF5] text-white' : 'bg-slate-100 text-slate-600'}`}>
                2. Preview
              </span>
              <span className="text-slate-300">→</span>
              <span className={`px-2 py-1 rounded ${bulkStep === 'results' ? 'bg-[#5B3DF5] text-white' : 'bg-slate-100 text-slate-600'}`}>
                3. Results
              </span>
            </div>

            {/* Step 1: Upload */}
            {bulkStep === 'upload' && (
              <div className="space-y-4">
                {/* Instructions */}
                <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700 border border-blue-100">
                  <p className="font-medium mb-2">CSV Requirements:</p>
                  <ul className="list-disc list-inside space-y-1 text-xs">
                    <li><strong>Required:</strong> Emp-ID, Emp-Name, Official Email ID, City, Role for the app Admin</li>
                    <li><strong>Status:</strong> Active or Inactive</li>
                    <li><strong>Date formats:</strong> DD-MMM-YY, DD-MMM-YYYY, DD/MM/YYYY, or YYYY-MM-DD</li>
                    <li><strong>App Roles:</strong> Rep, Manager, City Admin, Admin</li>
                  </ul>
                </div>

                {/* Download Template */}
                <Button 
                  variant="outline" 
                  onClick={handleDownloadTemplate}
                  className="w-full h-10 border-dashed"
                  data-testid="download-template-btn"
                >
                  <Download size={16} className="mr-2" />
                  Download CSV Template
                </Button>

                {/* File Upload */}
                <div className="space-y-2">
                  <Label>Upload CSV File</Label>
                  <div 
                    className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center hover:border-slate-300 transition-colors cursor-pointer"
                    onClick={() => fileInputRef.current?.click()}
                    data-testid="file-drop-zone"
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv"
                      onChange={handleFileUpload}
                      className="hidden"
                      data-testid="file-input"
                    />
                    <FileText size={32} className="mx-auto text-slate-400 mb-2" />
                    <p className="text-sm text-slate-600">Click to select CSV file</p>
                    <p className="text-xs text-slate-400 mt-1">or drag and drop</p>
                  </div>
                </div>

                {/* Or paste manually */}
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-slate-200"></div>
                  </div>
                  <div className="relative flex justify-center text-xs">
                    <span className="bg-white px-2 text-slate-500">or paste CSV content</span>
                  </div>
                </div>

                <div className="space-y-2">
                  <textarea
                    value={csvContent}
                    onChange={(e) => setCsvContent(e.target.value)}
                    placeholder="Emp-ID,Emp-Name,Status,DOJ,Go Live Date,Designation,Role,Role for the app Admin,Reporting Supervisor,Reporting Supervisor E-mail ID,Contact Number,Date of Birth,Official Email ID,Currently Operating Location"
                    className="w-full h-28 p-3 border rounded-lg text-sm font-mono resize-none focus:outline-none focus:ring-2 focus:ring-[#EF4444]"
                    data-testid="csv-textarea"
                  />
                </div>

                {csvContent && (
                  <div className="flex items-center gap-2 text-sm text-emerald-600">
                    <CheckCircle size={16} />
                    CSV content loaded ({csvContent.split('\n').length} lines)
                  </div>
                )}

                {isCityAdmin && (
                  <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                    Note: Only users for {userCity} will be processed.
                  </p>
                )}
              </div>
            )}

            {/* Step 2: Preview */}
            {bulkStep === 'preview' && previewData && (
              <div className="space-y-4">
                {/* Parse Error - show prominently */}
                {previewData.summary?.parse_error && (
                  <div className="bg-red-100 border border-red-300 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <AlertCircle size={20} className="text-red-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-semibold text-red-800">CSV Parsing Failed</p>
                        <p className="text-sm text-red-700 mt-1">{previewData.summary.parse_error}</p>
                        <p className="text-xs text-red-600 mt-2">Please check your CSV file format and try again.</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Summary - only show if no parse error */}
                {!previewData.summary?.parse_error && (
                  <div className="grid grid-cols-4 gap-2 text-center">
                    <div className="p-2 bg-slate-50 rounded">
                      <p className="text-lg font-bold text-slate-700">{previewData.summary?.total || 0}</p>
                      <p className="text-xs text-slate-500">Total Rows</p>
                    </div>
                    <div className="p-2 bg-emerald-50 rounded">
                      <p className="text-lg font-bold text-emerald-600">{previewData.summary?.to_create || 0}</p>
                      <p className="text-xs text-emerald-700">To Create</p>
                    </div>
                    <div className="p-2 bg-blue-50 rounded">
                      <p className="text-lg font-bold text-blue-600">{previewData.summary?.to_update || 0}</p>
                      <p className="text-xs text-blue-700">To Update</p>
                    </div>
                    <div className="p-2 bg-red-50 rounded">
                      <p className="text-lg font-bold text-[#5B3DF5]">{previewData.summary?.invalid || 0}</p>
                      <p className="text-xs text-red-700">Invalid</p>
                    </div>
                  </div>
                )}

                {/* Row-level Errors summary - only show if not a parse error */}
                {!previewData.summary?.parse_error && previewData.errors?.length > 0 && (
                  <div className="bg-red-50 border border-red-100 rounded-lg p-3">
                    <button 
                      onClick={() => setExpandedErrors(!expandedErrors)}
                      className="flex items-center justify-between w-full text-left"
                    >
                      <span className="text-sm font-medium text-red-700 flex items-center gap-2">
                        <AlertCircle size={16} />
                        {previewData.errors.length} rows have errors
                      </span>
                      {expandedErrors ? <ChevronUp size={16} className="text-[#5B3DF5]" /> : <ChevronDown size={16} className="text-[#5B3DF5]" />}
                    </button>
                    {expandedErrors && (
                      <div className="mt-2 max-h-32 overflow-y-auto space-y-1">
                        {previewData.errors.map((err, i) => (
                          <p key={i} className="text-xs text-[#5B3DF5]">
                            Row {err.row}: {err.errors.join('; ')}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Warnings summary */}
                {previewData.warnings?.length > 0 && (
                  <div className="bg-amber-50 border border-amber-100 rounded-lg p-3">
                    <p className="text-sm font-medium text-amber-700 flex items-center gap-2 mb-2">
                      <AlertTriangle size={16} />
                      {previewData.warnings.length} rows have warnings
                    </p>
                    <div className="max-h-24 overflow-y-auto space-y-1">
                      {previewData.warnings.slice(0, 5).map((warn, i) => (
                        <p key={i} className="text-xs text-amber-600">
                          Row {warn.row}: {warn.warnings.join('; ')}
                        </p>
                      ))}
                      {previewData.warnings.length > 5 && (
                        <p className="text-xs text-amber-500">...and {previewData.warnings.length - 5} more</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Preview Table */}
                <div className="border rounded-lg overflow-hidden">
                  <div className="max-h-64 overflow-auto">
                    <Table>
                      <TableHeader className="sticky top-0 bg-slate-50">
                        <TableRow>
                          <TableHead className="w-12 font-semibold">#</TableHead>
                          <TableHead className="font-semibold">Status</TableHead>
                          <TableHead className="font-semibold">Emp ID</TableHead>
                          <TableHead className="font-semibold">Name</TableHead>
                          <TableHead className="font-semibold">Email</TableHead>
                          <TableHead className="font-semibold">City</TableHead>
                          <TableHead className="font-semibold">Role</TableHead>
                          <TableHead className="font-semibold">Action</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {previewData.rows?.map((row) => (
                          <TableRow 
                            key={row.row_num} 
                            className={row.is_valid ? '' : 'bg-red-50'}
                          >
                            <TableCell className="text-xs text-slate-500">{row.row_num}</TableCell>
                            <TableCell>
                              {row.is_valid ? (
                                <CheckCircle size={16} className="text-emerald-500" />
                              ) : (
                                <div className="group relative">
                                  <AlertCircle size={16} className="text-[#5B3DF5]" />
                                  <div className="absolute left-0 top-6 z-10 hidden group-hover:block bg-[#4A2FE0] text-white text-xs p-2 rounded shadow-lg max-w-xs">
                                    {row.errors?.join('; ')}
                                  </div>
                                </div>
                              )}
                            </TableCell>
                            <TableCell className="text-xs font-mono">{row.emp_id || '—'}</TableCell>
                            <TableCell className="text-sm">{row.emp_name || '—'}</TableCell>
                            <TableCell className="text-xs">{row.email || '—'}</TableCell>
                            <TableCell className="text-xs">{row.city || '—'}</TableCell>
                            <TableCell>
                              <span className={`text-xs px-1.5 py-0.5 rounded ${
                                row.app_role === 'Rep' ? 'bg-slate-100' :
                                row.app_role === 'Manager' ? 'bg-blue-100 text-blue-700' :
                                row.app_role === 'city_admin' ? 'bg-amber-100 text-amber-700' :
                                row.app_role === 'Admin' ? 'bg-purple-100 text-purple-700' :
                                'bg-slate-100'
                              }`}>
                                {row.app_role || row.app_role_raw || '—'}
                              </span>
                            </TableCell>
                            <TableCell>
                              {row.is_valid && (
                                <span className={`text-xs px-1.5 py-0.5 rounded ${
                                  row.action === 'create' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'
                                }`}>
                                  {row.action === 'create' ? 'New' : 'Update'}
                                </span>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>

                <p className="text-xs text-slate-500">
                  {previewData.summary?.parse_error 
                    ? 'Please fix the CSV format and try again.'
                    : `Invalid rows will be skipped during upload. Valid rows: ${previewData.summary?.valid || 0}`
                  }
                </p>
              </div>
            )}

            {/* Step 3: Results */}
            {bulkStep === 'results' && bulkResults && (
              <div className="space-y-4">
                <div className="text-center py-4">
                  <CheckCircle size={48} className="mx-auto text-emerald-500 mb-3" />
                  <h3 className="text-lg font-semibold text-slate-800">Upload Complete!</h3>
                </div>

                {/* Results Summary */}
                <div className="grid grid-cols-4 gap-2 text-center">
                  <div className="p-3 bg-emerald-50 rounded-lg">
                    <p className="text-2xl font-bold text-emerald-600">{bulkResults.created || 0}</p>
                    <p className="text-xs text-emerald-700">Created</p>
                  </div>
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <p className="text-2xl font-bold text-blue-600">{bulkResults.updated || 0}</p>
                    <p className="text-xs text-blue-700">Updated</p>
                  </div>
                  <div className="p-3 bg-amber-50 rounded-lg">
                    <p className="text-2xl font-bold text-amber-600">{bulkResults.skipped || 0}</p>
                    <p className="text-xs text-amber-700">Skipped</p>
                  </div>
                  <div className="p-3 bg-red-50 rounded-lg">
                    <p className="text-2xl font-bold text-[#5B3DF5]">{bulkResults.failed || 0}</p>
                    <p className="text-xs text-red-700">Failed</p>
                  </div>
                </div>

                {/* Detailed errors */}
                {bulkResults.errors && bulkResults.errors.length > 0 && (
                  <div className="bg-red-50 border border-red-100 rounded-lg p-3">
                    <p className="text-sm font-medium text-red-700 mb-2">Errors:</p>
                    <div className="max-h-32 overflow-y-auto space-y-1">
                      {bulkResults.errors.map((err, i) => (
                        <p key={i} className="text-xs text-[#5B3DF5]">{err}</p>
                      ))}
                    </div>
                  </div>
                )}

                {/* Processed rows detail */}
                {bulkResults.processed_rows && bulkResults.processed_rows.length > 0 && (
                  <div className="border rounded-lg overflow-hidden">
                    <div className="max-h-48 overflow-auto">
                      <Table>
                        <TableHeader className="sticky top-0 bg-slate-50">
                          <TableRow>
                            <TableHead className="w-12 font-semibold">Row</TableHead>
                            <TableHead className="font-semibold">Status</TableHead>
                            <TableHead className="font-semibold">Email</TableHead>
                            <TableHead className="font-semibold">Details</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {bulkResults.processed_rows.map((row, i) => (
                            <TableRow key={i}>
                              <TableCell className="text-xs">{row.row}</TableCell>
                              <TableCell>
                                <span className={`text-xs px-1.5 py-0.5 rounded ${
                                  row.status === 'created' ? 'bg-emerald-100 text-emerald-700' :
                                  row.status === 'updated' ? 'bg-blue-100 text-blue-700' :
                                  row.status === 'skipped' ? 'bg-amber-100 text-amber-700' :
                                  'bg-red-100 text-red-700'
                                }`}>
                                  {row.status}
                                </span>
                              </TableCell>
                              <TableCell className="text-xs">{row.email || '—'}</TableCell>
                              <TableCell className="text-xs text-slate-500">{row.reason || '—'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 pt-4 border-t mt-4">
            {bulkStep === 'upload' && (
              <>
                <Button 
                  variant="outline" 
                  onClick={() => setShowBulkDialog(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handlePreviewCSV}
                  disabled={!csvContent.trim() || bulkLoading}
                  className="flex-1 bg-[#5B3DF5] hover:bg-[#4A2FE0]"
                  data-testid="preview-csv-btn"
                >
                  {bulkLoading ? (
                    <>
                      <Loader2 size={16} className="mr-2 animate-spin" />
                      Parsing...
                    </>
                  ) : (
                    <>
                      Preview Data
                    </>
                  )}
                </Button>
              </>
            )}

            {bulkStep === 'preview' && (
              <>
                <Button 
                  variant="outline" 
                  onClick={() => setBulkStep('upload')}
                  className="flex-1"
                >
                  ← Back
                </Button>
                <Button 
                  onClick={handleBulkUpload}
                  disabled={!previewData?.summary?.valid || bulkLoading}
                  className="flex-1 bg-[#5B3DF5] hover:bg-[#4A2FE0]"
                  data-testid="confirm-upload-btn"
                >
                  {bulkLoading ? (
                    <>
                      <Loader2 size={16} className="mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload size={16} className="mr-2" />
                      Upload {previewData?.summary?.valid || 0} Users
                    </>
                  )}
                </Button>
              </>
            )}

            {bulkStep === 'results' && (
              <>
                <Button 
                  variant="outline" 
                  onClick={resetBulkUpload}
                  className="flex-1"
                >
                  Upload More
                </Button>
                <Button 
                  onClick={() => setShowBulkDialog(false)}
                  className="flex-1 bg-[#5B3DF5] hover:bg-[#4A2FE0]"
                >
                  Done
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={showResetPasswordDialog} onOpenChange={setShowResetPasswordDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            {resetPasswordUser && (
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="text-sm text-slate-600">Resetting password for:</p>
                <p className="font-medium">{resetPasswordUser.full_name || resetPasswordUser.rep_name}</p>
                <p className="text-sm text-slate-500">{resetPasswordUser.email}</p>
              </div>
            )}
            <div className="space-y-2">
              <Label>New Password <span className="text-[#5B3DF5]">*</span></Label>
              <Input
                type="text"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                data-testid="reset-password-input"
              />
              <p className="text-xs text-slate-500">Minimum 6 characters. User should change this after first login.</p>
            </div>
            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowResetPasswordDialog(false);
                  setResetPasswordUser(null);
                  setNewPassword('');
                }}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleResetPassword}
                disabled={resetPasswordLoading || newPassword.length < 6}
                className="flex-1 bg-[#5B3DF5] hover:bg-[#4A2FE0]"
                data-testid="reset-password-submit-btn"
              >
                {resetPasswordLoading ? (
                  <><Loader2 size={16} className="animate-spin mr-2" /> Resetting...</>
                ) : (
                  'Reset Password'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Assign Manager Dialog */}
      <Dialog open={showBulkAssignDialog} onOpenChange={setShowBulkAssignDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCheck className="h-5 w-5 text-[#5B3DF5]" />
              Bulk Assign Reporting Manager
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex flex-col gap-4 flex-1 overflow-hidden">
            {/* Filters */}
            <div className="flex gap-4 items-end">
              <div className="flex-1">
                <Label className="text-sm mb-1.5 block">Filter by City</Label>
                <Select 
                  value={unassignedCity} 
                  onValueChange={(v) => {
                    const newCity = v === 'all' ? '' : v;
                    setUnassignedCity(newCity);
                    setSelectedReps([]);
                    setBulkAssignManagerId(''); // Reset manager selection
                    fetchUnassignedReps(newCity);
                    fetchManagers(newCity); // Fetch managers for the new city
                  }}
                  disabled={isCityAdmin}
                >
                  <SelectTrigger className="h-10">
                    <SelectValue placeholder="All Cities" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Cities</SelectItem>
                    {CITIES.map(city => (
                      <SelectItem key={city} value={city}>{city}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1">
                <Label className="text-sm mb-1.5 block">Assign to Manager</Label>
                <Select value={bulkAssignManagerId} onValueChange={setBulkAssignManagerId}>
                  <SelectTrigger className="h-10">
                    <SelectValue placeholder="Select Manager" />
                  </SelectTrigger>
                  <SelectContent>
                    {managers.filter(m => !unassignedCity || m.city === unassignedCity).map(m => (
                      <SelectItem key={m.user_id} value={m.user_id}>
                        {m.full_name || m.name} ({m.city})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                onClick={handleBulkAssignManager}
                disabled={bulkAssignLoading || selectedReps.length === 0 || !bulkAssignManagerId}
                className="h-10 bg-[#5B3DF5] hover:bg-[#4A2FE0]"
              >
                {bulkAssignLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Assign {selectedReps.length > 0 ? `(${selectedReps.length})` : ''}
              </Button>
            </div>

            {/* Stats */}
            <div className="flex items-center gap-4 text-sm">
              <span className="text-slate-500">
                {unassignedReps.length} unassigned reps {unassignedCity && `in ${unassignedCity}`}
              </span>
              {selectedReps.length > 0 && (
                <span className="text-[#5B3DF5] font-medium">
                  {selectedReps.length} selected
                </span>
              )}
            </div>

            {/* Reps Table */}
            <div className="flex-1 overflow-auto border rounded-lg">
              {bulkAssignLoading ? (
                <div className="p-8 text-center">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto text-[#5B3DF5]" />
                  <p className="text-sm text-slate-500 mt-2">Loading unassigned reps...</p>
                </div>
              ) : unassignedReps.length === 0 ? (
                <div className="p-8 text-center">
                  <CheckCircle className="h-12 w-12 text-green-400 mx-auto mb-3" />
                  <p className="text-slate-600 font-medium">All reps are assigned!</p>
                  <p className="text-sm text-slate-500 mt-1">
                    No unassigned reps found {unassignedCity && `in ${unassignedCity}`}
                  </p>
                </div>
              ) : (
                <Table>
                  <TableHeader className="sticky top-0 bg-slate-50">
                    <TableRow>
                      <TableHead className="w-12">
                        <input
                          type="checkbox"
                          checked={selectedReps.length === unassignedReps.length && unassignedReps.length > 0}
                          onChange={(e) => handleSelectAllReps(e.target.checked)}
                          className="h-4 w-4 rounded border-slate-300"
                        />
                      </TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>City</TableHead>
                      <TableHead>Role</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {unassignedReps.map(rep => (
                      <TableRow key={rep.user_id} className="hover:bg-slate-50">
                        <TableCell>
                          <input
                            type="checkbox"
                            checked={selectedReps.includes(rep.user_id)}
                            onChange={(e) => handleSelectRep(rep.user_id, e.target.checked)}
                            className="h-4 w-4 rounded border-slate-300"
                          />
                        </TableCell>
                        <TableCell className="font-medium">
                          {rep.full_name || rep.rep_name || rep.name || 'N/A'}
                        </TableCell>
                        <TableCell className="text-slate-500 text-sm">{rep.email}</TableCell>
                        <TableCell>{rep.city || 'N/A'}</TableCell>
                        <TableCell>
                          <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs">
                            {rep.role}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUsers;
